import type { SegmentAnalysisDataSource, SegmentAnalysisLoadStatus } from "../types"

import { uiOption } from "@/lib/ui-options"

/**
 * Tenant opt-in for the Segment Analysis demo dataset: the admin's branding
 * setting (`ui_options.segment_demo_data`, #3225) when there is one, else the
 * tenant's `customer/config.json`. The backend reads the same order, so the
 * page and the server agree on whether a tenant is a demo tenant.
 *
 * When `config.segment_demo_data` is true (internal builds and tenants that
 * explicitly demo the module), the module keeps its historical behavior: the
 * bundled mock portfolio backs the UI whenever the live backend has no
 * segments, and the demo affordances (role/persona pickers, mock-data switch)
 * are available.
 *
 * When the key is absent or false — the customer default — the module is
 * live-data only: a ready-but-empty backend renders a real empty state and the
 * demo affordances are hidden, so demo content never leaks to tenants that
 * did not opt in (#2768).
 */
export const SEGMENT_DEMO_DATA: boolean = uiOption("segment_demo_data")

export type SegmentPortfolioSource = {
  /** True when the backend payload is the portfolio (also ready-but-empty for live-only tenants). */
  usesBackendPortfolio: boolean
  /** What backs the portfolio when the backend does not: the demo dataset or nothing. */
  fallback: "demo" | "empty"
  dataSource: SegmentAnalysisDataSource
}

/**
 * Decides what backs the portfolio. Pure so the whole matrix is unit-testable;
 * the hook passes `SEGMENT_DEMO_DATA` as `demoDataEnabled`.
 *
 * Demo tenants keep the historical behavior: the mock portfolio backs the UI
 * whenever the backend has no segments ("mock-fallback"). For everyone else a
 * ready-but-empty backend IS the portfolio — the mock data is another tenant's
 * demo content and must never leak (#2768). This also routes first-segment
 * creation to the backend instead of throwaway local state.
 */
export function resolveSegmentPortfolioSource(input: {
  useMockData: boolean
  backendStatus: SegmentAnalysisLoadStatus
  backendSegmentCount: number
  demoDataEnabled: boolean
}): SegmentPortfolioSource {
  const { useMockData, backendStatus, backendSegmentCount, demoDataEnabled } = input
  const usesBackendPortfolio =
    !useMockData && backendStatus === "ready" && (backendSegmentCount > 0 || !demoDataEnabled)
  const fallback = demoDataEnabled ? "demo" : "empty"
  let dataSource: SegmentAnalysisDataSource = "mock-fallback"
  if (useMockData) {
    dataSource = "mock"
  } else if (usesBackendPortfolio) {
    dataSource = "backend"
  }

  return { usesBackendPortfolio, fallback, dataSource }
}
